import React, { useState } from 'react';
import { 
  Home, 
  Leaf, 
  Calendar, 
  Droplets, 
  Sun, 
  Check, 
  Plus, 
  Cloud, 
  Bell
} from 'lucide-react';

const InteractiveDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  
  return (
    <div className="mock-dashboard bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-100 w-full max-w-sm mx-auto">
      {/* Mini navigation */}
      <div className="mini-nav bg-gray-50 border-b border-gray-200 p-3">
        <div className="flex items-center justify-around">
          <div 
            className={`nav-item flex flex-col items-center gap-1 p-2 rounded-lg cursor-pointer transition-all relative ${
              activeTab === 'overview' 
                ? 'bg-emerald-100 text-emerald-700' 
                : 'text-gray-600 hover:text-emerald-600 hover:bg-emerald-50'
            }`}
            onClick={() => setActiveTab('overview')}
          >
            <Home size={16} />
            <span className="text-xs font-medium">Pradžia</span>
            {/* Notification indicator */}
            <div className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
          </div>
          <div 
            className={`nav-item flex flex-col items-center gap-1 p-2 rounded-lg cursor-pointer transition-all ${
              activeTab === 'plants' 
                ? 'bg-emerald-100 text-emerald-700' 
                : 'text-gray-600 hover:text-emerald-600 hover:bg-emerald-50'
            }`}
            onClick={() => setActiveTab('plants')}
          >
            <Leaf size={16} />
            <span className="text-xs font-medium">Augalai</span>
          </div>
          <div 
            className={`nav-item flex flex-col items-center gap-1 p-2 rounded-lg cursor-pointer transition-all ${
              activeTab === 'calendar' 
                ? 'bg-emerald-100 text-emerald-700' 
                : 'text-gray-600 hover:text-emerald-600 hover:bg-emerald-50'
            }`}
            onClick={() => setActiveTab('calendar')}
          >
            <Calendar size={16} />
            <span className="text-xs font-medium">Kalendorius</span>
          </div>
        </div>
      </div>
      
      {/* Dashboard content */}
      <div className="dashboard-main p-4 space-y-4">
        {activeTab === 'overview' && (
          <>
            {/* Today's tasks */}
            <div className="today-section">
              <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <Bell size={14} />
                Šiandienos darbai
              </h3>
              <div className="task-list space-y-2">
                <div className="task-item bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center gap-3 hover:bg-blue-100 hover:shadow-md transition-all duration-200 cursor-pointer group">
                  <div className="task-icon water bg-blue-500 p-2 rounded-full group-hover:scale-110 transition-transform">
                    <Droplets size={12} className="text-white" />
                  </div>
                  <span className="text-sm text-gray-700 flex-1">Palaistyti rožes</span>
                  <span className="time text-xs text-blue-600 font-medium">09:00</span>
                </div>
                <div className="task-item bg-orange-50 border border-orange-200 rounded-lg p-3 flex items-center gap-3 hover:bg-orange-100 hover:shadow-md transition-all duration-200 cursor-pointer group">
                  <div className="task-icon sun bg-orange-500 p-2 rounded-full group-hover:scale-110 transition-transform">
                    <Sun size={12} className="text-white" />
                  </div>
                  <span className="text-sm text-gray-700 flex-1">Perkelti orchidėjas į pavėsį</span>
                  <span className="time text-xs text-orange-600 font-medium">14:00</span>
                </div>
              </div>
            </div>
            
            {/* My plants grid */}
            <div className="plants-section">
              <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <Leaf size={14} />
                Mano augalai
              </h3>
              <div className="plants-grid grid grid-cols-3 gap-2">
                <div className="plant-card bg-gradient-to-br from-pink-100 to-red-100 border border-pink-200 rounded-lg p-2 text-center hover:shadow-md transition-all duration-300 hover:scale-105 cursor-pointer group">
                  <div className="plant-image rose w-8 h-8 bg-gradient-to-br from-pink-400 to-red-400 rounded-full mx-auto mb-1 animate-breathe"></div>
                  <span className="text-xs text-gray-700 font-medium">Rožė "Queen"</span>
                  <div className="plant-status text-xs text-blue-600 mt-1 flex items-center justify-center gap-1">
                    <Droplets size={10} className="animate-water-drop" /> Rytoj
                  </div>
                </div>
                <div className="plant-card bg-gradient-to-br from-green-100 to-emerald-100 border border-green-200 rounded-lg p-2 text-center hover:shadow-md transition-all duration-300 hover:scale-105 cursor-pointer group">
                  <div className="plant-image tomato w-8 h-8 bg-gradient-to-br from-green-400 to-emerald-400 rounded-full mx-auto mb-1 animate-breathe"></div>
                  <span className="text-xs text-gray-700 font-medium">Pomidorai</span>
                  <div className="plant-status good text-xs text-green-600 mt-1 flex items-center justify-center gap-1">
                    <Check size={10} className="text-green-500" /> Gerai
                  </div>
                </div>
                <div className="plant-card add-new bg-gradient-to-br from-gray-100 to-gray-200 border border-gray-300 rounded-lg p-2 text-center hover:bg-gray-200 transition-colors cursor-pointer">
                  <Plus size={16} className="text-gray-500 mx-auto mb-1" />
                  <span className="text-xs text-gray-600">Pridėti augalą</span>
                </div>
              </div>
            </div>
            
            {/* Weather widget */}
            <div className="weather-widget bg-gradient-to-br from-blue-50 to-cyan-50 border border-blue-200 rounded-lg p-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Cloud className="weather-icon text-blue-500" size={20} />
                <div>
                  <span className="temp text-lg font-bold text-gray-800">22°C</span>
                  <span className="desc text-xs text-gray-600 block">Debesuota</span>
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs text-gray-600">Drėgmė: 65%</div>
                <div className="text-xs text-gray-600">Vėjas: 12 km/h</div>
              </div>
            </div>
          </>
        )}

        {activeTab === 'plants' && (
          <div className="plants-detail space-y-3">
            <div className="plant-detail-card bg-gradient-to-br from-pink-50 to-red-50 border border-pink-200 rounded-lg p-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-pink-400 to-red-400 rounded-full"></div>
                <div className="flex-1">
                  <h4 className="font-medium text-gray-800">Rožė "Queen"</h4>
                  <p className="text-xs text-gray-600">Rosa hybrida</p>
                </div>
                <div className="text-right">
                  <div className="text-xs text-blue-600 font-medium">Rytoj laistyti</div>
                  <div className="text-xs text-gray-500">Drėgmė: 45%</div>
                </div>
              </div>
            </div>
            
            <div className="plant-detail-card bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-lg p-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-emerald-400 rounded-full"></div>
                <div className="flex-1">
                  <h4 className="font-medium text-gray-800">Pomidorai</h4>
                  <p className="text-xs text-gray-600">Solanum lycopersicum</p>
                </div>
                <div className="text-right">
                  <div className="text-xs text-green-600 font-medium">Sveiki</div>
                  <div className="text-xs text-gray-500">Drėgmė: 78%</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'calendar' && (
          <div className="calendar-preview space-y-3">
            <div className="calendar-header flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-800">Rugpjūtis 2024</h3>
              <div className="flex gap-1">
                <button className="w-6 h-6 rounded bg-gray-100 hover:bg-gray-200 text-xs">‹</button>
                <button className="w-6 h-6 rounded bg-gray-100 hover:bg-gray-200 text-xs">›</button>
              </div>
            </div>
            
            <div className="calendar-grid grid grid-cols-7 gap-1 text-xs">
              {['P', 'A', 'T', 'K', 'P', 'Š', 'S'].map(day => (
                <div key={day} className="text-center text-gray-500 font-medium p-1">{day}</div>
              ))}
              {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                <div 
                  key={day} 
                  className={`text-center p-1 rounded cursor-pointer hover:bg-gray-100 ${
                    day === 15 ? 'bg-emerald-100 text-emerald-700 font-medium' : 'text-gray-700'
                  }`}
                >
                  {day}
                  {day === 15 && <div className="w-1 h-1 bg-emerald-500 rounded-full mx-auto mt-1"></div>}
                </div>
              ))}
            </div>
            
            <div className="calendar-events space-y-2">
              <div className="event-item bg-emerald-50 border border-emerald-200 rounded p-2">
                <div className="text-xs font-medium text-emerald-700">Rugpjūčio 15</div>
                <div className="text-xs text-gray-600">Palaistyti rožes, tręšti pomidorus</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Pro features blur overlay */}
      <div className="pro-overlay absolute inset-0 bg-gradient-to-t from-white/80 to-transparent pointer-events-none">
        <div className="absolute bottom-2 right-2 bg-emerald-600 text-white text-xs px-2 py-1 rounded-full font-medium">
          PRO
        </div>
      </div>
    </div>
  );
};

export default InteractiveDashboard; 